import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ChevronLeft, ChevronRight, Plus, X, Clock, MapPin, 
  Calendar as CalendarIcon, Trash2, Edit2, Check, 
  MoreHorizontal, Bell, Repeat, ChevronDown, GripVertical,
  Settings, Eye, EyeOff, Palette
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Color presets (Google Calendar colors)
const COLOR_PRESETS = [
  '#4285f4', '#ea4335', '#fbbc04', '#34a853', '#ff6d01',
  '#46bdc6', '#7986cb', '#8e24aa', '#e67c73', '#f6bf26',
  '#33b679', '#039be5', '#616161', '#3f51b5', '#0b8043'
];

// Helper functions
const formatDate = (date) => {
  return date.toLocaleDateString('ru-RU', { 
    weekday: 'short', 
    day: 'numeric', 
    month: 'short' 
  });
};

const formatTime = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
};

const isSameDay = (d1, d2) => {
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

// Mini Calendar Component
const MiniCalendar = ({ currentDate, onDateSelect, events }) => {
  const [viewDate, setViewDate] = useState(new Date(currentDate));
  
  useEffect(() => {
    setViewDate(new Date(currentDate));
  }, [currentDate]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1; // Monday start

  const days = [];
  const prevMonthDays = getDaysInMonth(year, month - 1);
  
  // Previous month days
  for (let i = adjustedFirstDay - 1; i >= 0; i--) {
    days.push({ day: prevMonthDays - i, isCurrentMonth: false, date: new Date(year, month - 1, prevMonthDays - i) });
  }
  
  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
  }
  
  // Next month days
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ day: i, isCurrentMonth: false, date: new Date(year, month + 1, i) });
  }

  const hasEvents = (date) => {
    // For trips, check by date string
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return events.some(e => e.date === dateStr);
  };

  const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 
                      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <button 
          onClick={() => setViewDate(new Date(year, month - 1, 1))}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
        <span className="font-medium text-gray-800 text-sm">
          {monthNames[month]} {year}
        </span>
        <button 
          onClick={() => setViewDate(new Date(year, month + 1, 1))}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
      </div>
      
      <div className="grid grid-cols-7 gap-0.5 text-center mb-1">
        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(d => (
          <div key={d} className="text-[10px] font-medium text-gray-400 py-1">{d}</div>
        ))}
      </div>
      
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((d, i) => {
          const isToday = isSameDay(d.date, new Date());
          const isSelected = isSameDay(d.date, currentDate);
          const hasEvent = hasEvents(d.date);
          
          return (
            <button
              key={i}
              onClick={() => onDateSelect(d.date)}
              className={`
                relative h-7 w-7 text-xs rounded-full transition-all duration-200
                ${d.isCurrentMonth ? 'text-gray-800' : 'text-gray-300'}
                ${isToday ? 'bg-blue-500 text-white font-bold' : ''}
                ${isSelected && !isToday ? 'bg-blue-100 text-blue-600 font-semibold' : ''}
                ${!isToday && !isSelected ? 'hover:bg-gray-100' : ''}
              `}
            >
              {d.day}
              {hasEvent && !isToday && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-500" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Event Form Modal
const EventModal = ({ isOpen, onClose, event, onSave, onDelete, calendars }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start: '',
    end: '',
    all_day: false,
    color: '#4285f4',
    location: '',
    recurrence: 'none',
    recurrence_end: '',
    reminders: [30],
    calendar_id: 'default'
  });
  const [showColorPicker, setShowColorPicker] = useState(false);

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
        description: event.description || '',
        start: event.start ? event.start.slice(0, 16) : '',
        end: event.end ? event.end.slice(0, 16) : '',
        all_day: event.all_day || false,
        color: event.color || '#4285f4',
        location: event.location || '',
        recurrence: event.recurrence || 'none',
        recurrence_end: event.recurrence_end || '',
        reminders: event.reminders || [30],
        calendar_id: event.calendar_id || 'default'
      });
    } else {
      // New event - set default times
      const now = new Date();
      now.setMinutes(0, 0, 0);
      const end = new Date(now);
      end.setHours(end.getHours() + 1);
      
      setFormData({
        title: '',
        description: '',
        start: now.toISOString().slice(0, 16),
        end: end.toISOString().slice(0, 16),
        all_day: false,
        color: '#4285f4',
        location: '',
        recurrence: 'none',
        recurrence_end: '',
        reminders: [30],
        calendar_id: 'default'
      });
    }
  }, [event]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Введите название события');
      return;
    }
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            {event?.id ? 'Редактировать событие' : 'Новое событие'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Title */}
          <input
            type="text"
            placeholder="Добавить название"
            value={formData.title}
            onChange={e => setFormData({...formData, title: e.target.value})}
            className="w-full px-0 py-2 text-xl font-medium border-0 border-b-2 border-gray-200 focus:border-blue-500 focus:outline-none focus:ring-0 transition-colors"
            autoFocus
          />

          {/* Date & Time */}
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-gray-400 mt-2.5" />
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="datetime-local"
                  value={formData.start}
                  onChange={e => setFormData({...formData, start: e.target.value})}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                />
                <span className="text-gray-400">—</span>
                <input
                  type="datetime-local"
                  value={formData.end}
                  onChange={e => setFormData({...formData, end: e.target.value})}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.all_day}
                  onChange={e => setFormData({...formData, all_day: e.target.checked})}
                  className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">Весь день</span>
              </label>
            </div>
          </div>

          {/* Location */}
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Добавить место"
              value={formData.location}
              onChange={e => setFormData({...formData, location: e.target.value})}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
            />
          </div>

          {/* Description */}
          <div className="flex items-start gap-3">
            <Edit2 className="w-5 h-5 text-gray-400 mt-2.5" />
            <textarea
              placeholder="Добавить описание"
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              rows={3}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm resize-none"
            />
          </div>

          {/* Calendar Selection */}
          <div className="flex items-center gap-3">
            <CalendarIcon className="w-5 h-5 text-gray-400" />
            <select
              value={formData.calendar_id}
              onChange={e => setFormData({...formData, calendar_id: e.target.value})}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
            >
              {calendars.map(cal => (
                <option key={cal.id} value={cal.id}>{cal.name}</option>
              ))}
            </select>
          </div>

          {/* Color Picker */}
          <div className="flex items-center gap-3">
            <Palette className="w-5 h-5 text-gray-400" />
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowColorPicker(!showColorPicker)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span 
                  className="w-4 h-4 rounded-full" 
                  style={{ backgroundColor: formData.color }}
                />
                <span className="text-sm text-gray-600">Цвет</span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>
              
              {showColorPicker && (
                <div className="absolute top-full left-0 mt-1 p-2 bg-white rounded-lg shadow-lg border z-10 grid grid-cols-5 gap-1">
                  {COLOR_PRESETS.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => {
                        setFormData({...formData, color});
                        setShowColorPicker(false);
                      }}
                      className={`w-6 h-6 rounded-full transition-transform hover:scale-110 ${formData.color === color ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recurrence */}
          <div className="flex items-center gap-3">
            <Repeat className="w-5 h-5 text-gray-400" />
            <select
              value={formData.recurrence}
              onChange={e => setFormData({...formData, recurrence: e.target.value})}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
            >
              <option value="none">Не повторять</option>
              <option value="daily">Каждый день</option>
              <option value="weekly">Каждую неделю</option>
              <option value="monthly">Каждый месяц</option>
              <option value="yearly">Каждый год</option>
            </select>
          </div>

          {formData.recurrence !== 'none' && (
            <div className="flex items-center gap-3 pl-8">
              <span className="text-sm text-gray-600">До:</span>
              <input
                type="date"
                value={formData.recurrence_end}
                onChange={e => setFormData({...formData, recurrence_end: e.target.value})}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
              />
            </div>
          )}

          {/* Reminder */}
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-gray-400" />
            <select
              value={formData.reminders[0]}
              onChange={e => setFormData({...formData, reminders: [parseInt(e.target.value)]})}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
            >
              <option value={0}>Без напоминания</option>
              <option value={5}>За 5 минут</option>
              <option value={10}>За 10 минут</option>
              <option value={15}>За 15 минут</option>
              <option value={30}>За 30 минут</option>
              <option value={60}>За 1 час</option>
              <option value={1440}>За 1 день</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            {event?.id ? (
              <button
                type="button"
                onClick={() => onDelete(event.id)}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Удалить
              </button>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Отмена
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                Сохранить
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Trip Card Component (for month/week views)
const TripCard = ({ trip, onClick, view }) => {
  // Color based on trip status or international flag
  const getColor = () => {
    if (trip.is_international) return '#8b5cf6'; // Purple for international
    if (trip.status === 'completed') return '#10b981'; // Green for completed
    if (trip.status === 'cancelled') return '#ef4444'; // Red for cancelled
    return '#3b82f6'; // Blue default
  };
  
  const color = getColor();
  
  return (
    <div
      onClick={() => onClick(trip)}
      className={`
        group cursor-pointer rounded-md px-1.5 py-0.5 text-xs font-medium truncate
        transition-all duration-150 hover:shadow-md
        ${view === 'month' ? 'mb-0.5' : 'mb-1'}
      `}
      style={{ 
        backgroundColor: color + '20',
        borderLeft: `3px solid ${color}`,
        color: color
      }}
    >
      <span className="opacity-80 mr-1">{trip.time_start || '—'}</span>
      <span className="font-semibold">{trip.route_name || 'Без маршрута'}</span>
    </div>
  );
};

// Main Calendar Component
export default function CalendarV2({ fullWidth = false }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // month, week, day
  const [events, setEvents] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [showSidebar, setShowSidebar] = useState(!fullWidth); // Hide sidebar in fullWidth mode

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Calculate date range based on view
      const start = new Date(currentDate);
      const end = new Date(currentDate);
      
      if (view === 'month') {
        start.setDate(1);
        start.setDate(start.getDate() - 7);
        end.setMonth(end.getMonth() + 1, 0);
        end.setDate(end.getDate() + 7);
      } else if (view === 'week') {
        const day = start.getDay();
        start.setDate(start.getDate() - (day === 0 ? 6 : day - 1));
        end.setDate(start.getDate() + 6);
      } else if (view === 'day') {
        // For day view, get the whole day
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
      }
      
      // Load TRIPS instead of calendar events for all views
      const startStr = formatDateForAPI(start);
      const endStr = formatDateForAPI(end);
      
      const [tripsRes, calendarsRes, driversRes, busesRes] = await Promise.all([
        axios.get(`${API}/api/trips`, {
          params: { date_from: startStr, date_to: endStr },
          ...getAuthHeaders()
        }),
        axios.get(`${API}/api/calendar/calendars`, getAuthHeaders()),
        axios.get(`${API}/api/users`, getAuthHeaders()),
        axios.get(`${API}/api/buses`, getAuthHeaders())
      ]);
      
      setEvents(tripsRes.data || []);
      setCalendars(calendarsRes.data);
      setDrivers(driversRes.data?.filter(u => u.is_driver === true) || []);
      setBuses(busesRes.data || []);
    } catch (error) {
      console.error('Error loading calendar data:', error);
      toast.error('Ошибка загрузки календаря');
    } finally {
      setLoading(false);
    }
  }, [currentDate, view]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Event handlers
  const handleCreateEvent = (date) => {
    const start = new Date(date);
    start.setHours(9, 0, 0, 0);
    const end = new Date(start);
    end.setHours(10, 0, 0, 0);
    
    setSelectedEvent({
      start: start.toISOString(),
      end: end.toISOString()
    });
    setShowEventModal(true);
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleSaveEvent = async (formData) => {
    try {
      if (selectedEvent?.id) {
        await axios.put(
          `${API}/api/calendar/events/${selectedEvent.id}`,
          formData,
          getAuthHeaders()
        );
        toast.success('Событие обновлено');
      } else {
        await axios.post(
          `${API}/api/calendar/events`,
          formData,
          getAuthHeaders()
        );
        toast.success('Событие создано');
      }
      setShowEventModal(false);
      setSelectedEvent(null);
      loadData();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error('Ошибка сохранения события');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Удалить событие?')) return;
    
    try {
      await axios.delete(
        `${API}/api/calendar/events/${eventId}`,
        getAuthHeaders()
      );
      toast.success('Событие удалено');
      setShowEventModal(false);
      setSelectedEvent(null);
      loadData();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Ошибка удаления события');
    }
  };

  // Drag & Drop
  const handleDragStart = (e, event) => {
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, date) => {
    e.preventDefault();
    if (!draggedEvent) return;

    const originalStart = new Date(draggedEvent.start);
    const originalEnd = new Date(draggedEvent.end);
    const duration = originalEnd - originalStart;

    const newStart = new Date(date);
    newStart.setHours(originalStart.getHours(), originalStart.getMinutes());
    const newEnd = new Date(newStart.getTime() + duration);

    try {
      await axios.put(
        `${API}/api/calendar/events/${draggedEvent.id}`,
        {
          start: newStart.toISOString(),
          end: newEnd.toISOString()
        },
        getAuthHeaders()
      );
      toast.success('Событие перемещено');
      loadData();
    } catch (error) {
      console.error('Error moving event:', error);
      toast.error('Ошибка перемещения события');
    }
    setDraggedEvent(null);
  };

  // Navigation
  const navigatePrev = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') newDate.setMonth(newDate.getMonth() - 1);
    else if (view === 'week') newDate.setDate(newDate.getDate() - 7);
    else newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') newDate.setMonth(newDate.getMonth() + 1);
    else if (view === 'week') newDate.setDate(newDate.getDate() + 7);
    else newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  // Get trips for a day (trips have 'date' field, not 'start'/'end')
  const getTripsForDay = (date) => {
    const dateStr = formatDateForAPI(date);
    return events.filter(trip => trip.date === dateStr);
  };

  // Alias for backward compatibility
  const getEventsForDay = getTripsForDay;

  // Render month view
  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;

    const days = [];
    const prevMonthDays = getDaysInMonth(year, month - 1);
    
    for (let i = adjustedFirstDay - 1; i >= 0; i--) {
      days.push({ day: prevMonthDays - i, isCurrentMonth: false, date: new Date(year, month - 1, prevMonthDays - i) });
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
    }
    
    const remaining = Math.ceil(days.length / 7) * 7 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ day: i, isCurrentMonth: false, date: new Date(year, month + 1, i) });
    }

    return (
      <div className="flex-1 overflow-hidden">
        {/* Week days header */}
        <div className="grid grid-cols-7 border-b bg-gray-50">
          {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(day => (
            <div key={day} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 flex-1" style={{ gridAutoRows: '1fr' }}>
          {days.map((d, i) => {
            const isToday = isSameDay(d.date, new Date());
            const dayTrips = getTripsForDay(d.date);
            
            return (
              <div
                key={i}
                className={`
                  border-b border-r min-h-[100px] p-1 transition-colors cursor-pointer
                  ${d.isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                  ${isToday ? 'bg-blue-50/50' : ''}
                  hover:bg-gray-50
                `}
                onClick={() => {
                  setCurrentDate(d.date);
                  setView('day');
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className={`
                    w-7 h-7 flex items-center justify-center rounded-full text-sm
                    ${isToday ? 'bg-blue-500 text-white font-bold' : ''}
                    ${!d.isCurrentMonth ? 'text-gray-400' : 'text-gray-700'}
                  `}>
                    {d.day}
                  </div>
                  {dayTrips.length > 0 && (
                    <span className="text-xs text-gray-400 font-medium">
                      {dayTrips.length}
                    </span>
                  )}
                </div>
                
                <div className="space-y-0.5 overflow-hidden" onClick={e => e.stopPropagation()}>
                  {dayTrips.slice(0, 3).map(trip => (
                    <TripCard
                      key={trip.id}
                      trip={trip}
                      onClick={() => {
                        setCurrentDate(d.date);
                        setView('day');
                      }}
                      view="month"
                    />
                  ))}
                  {dayTrips.length > 3 && (
                    <div className="text-xs text-gray-500 pl-2">
                      +{dayTrips.length - 3} ещё
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render week view - shows trips for each day of the week
  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - (day === 0 ? 6 : day - 1));
    
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(date.getDate() + i);
      weekDays.push(date);
    }

    return (
      <div className="flex-1 overflow-auto bg-gray-50 p-4">
        <div className="grid grid-cols-7 gap-2">
          {weekDays.map((date, i) => {
            const isToday = isSameDay(date, new Date());
            const dayTrips = getTripsForDay(date);
            const sortedTrips = [...dayTrips].sort((a, b) => 
              (a.time_start || '').localeCompare(b.time_start || '')
            );
            
            return (
              <div 
                key={i} 
                className={`
                  bg-white rounded-lg shadow-sm border overflow-hidden
                  ${isToday ? 'ring-2 ring-blue-500' : ''}
                `}
              >
                {/* Day header */}
                <div 
                  className={`
                    px-3 py-2 text-center cursor-pointer hover:bg-gray-50 transition-colors
                    ${isToday ? 'bg-blue-500 text-white' : 'bg-gray-100'}
                  `}
                  onClick={() => {
                    setCurrentDate(date);
                    setView('day');
                  }}
                >
                  <div className="text-xs uppercase font-medium opacity-80">
                    {date.toLocaleDateString('ru-RU', { weekday: 'short' })}
                  </div>
                  <div className="text-lg font-bold">
                    {date.getDate()}
                  </div>
                  {dayTrips.length > 0 && (
                    <div className={`text-xs mt-0.5 ${isToday ? 'text-blue-100' : 'text-gray-500'}`}>
                      {dayTrips.length} {dayTrips.length === 1 ? 'рейс' : dayTrips.length < 5 ? 'рейса' : 'рейсов'}
                    </div>
                  )}
                </div>
                
                {/* Trips list */}
                <div className="p-2 space-y-1 max-h-[400px] overflow-y-auto">
                  {sortedTrips.length === 0 ? (
                    <div className="text-center text-gray-400 text-xs py-4">
                      Нет рейсов
                    </div>
                  ) : (
                    sortedTrips.map(trip => (
                      <div
                        key={trip.id}
                        onClick={() => {
                          setCurrentDate(date);
                          setView('day');
                        }}
                        className={`
                          p-2 rounded-md text-xs cursor-pointer transition-all hover:shadow-md
                          ${trip.is_international ? 'bg-purple-50 border-l-3 border-purple-500' : 'bg-blue-50 border-l-3 border-blue-500'}
                        `}
                        style={{ borderLeftWidth: '3px' }}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono font-semibold text-gray-700">
                            {trip.time_start || '—'}
                          </span>
                          {trip.is_international && (
                            <span className="text-purple-500">🌍</span>
                          )}
                        </div>
                        <div className="font-medium text-gray-800 truncate mt-0.5">
                          {trip.route_name || 'Без маршрута'}
                        </div>
                        {trip.bus_number && (
                          <div className="text-gray-500 truncate">
                            🚌 {trip.bus_number}
                          </div>
                        )}
                        {(trip.payment_amount > 0 || trip.driver_salary > 0) && (
                          <div className="flex gap-2 mt-1 text-gray-500">
                            {trip.payment_amount > 0 && <span>€{trip.payment_amount}</span>}
                            {trip.driver_salary > 0 && <span className="text-green-600">ЗП: €{trip.driver_salary}</span>}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Day view columns definition (Excel-style for TRIPS)
  const TRIP_COLUMNS = [
    { key: 'notes', label: 'Заметки', width: 100, type: 'textarea' },
    { key: 'contact_email', label: 'Klijent', width: 120, type: 'text' },
    { key: 'date', label: 'Дата', width: 90, type: 'date' },
    { key: 'time_start', label: 'Время', width: 65, type: 'time' },
    { key: 'route_name', label: 'Маршрут', width: 120, type: 'text' },
    { key: 'description', label: 'Описание', width: 130, type: 'textarea' },
    { key: 'start_name', label: 'Откуда', width: 90, type: 'textarea' },
    { key: 'end_name', label: 'Куда', width: 90, type: 'textarea' },
    { key: 'bus_id', label: 'Автобус', width: 100, type: 'bus_select' },
    { key: 'driver_id', label: 'Водитель', width: 120, type: 'driver_select' },
    { key: 'payment_amount', label: 'Оплата €', width: 80, type: 'number' },
    { key: 'contact_phone', label: 'Телефон', width: 100, type: 'text' },
    { key: 'driver_salary', label: 'Зарплата €', width: 90, type: 'number' },
    { key: 'notes2', label: 'Zametki 2', width: 100, type: 'textarea' },
  ];

  // State for trips in day view
  const [dayTrips, setDayTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [buses, setBuses] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [tripsLoading, setTripsLoading] = useState(false);

  // Format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load trips for the selected day
  const loadDayTrips = useCallback(async () => {
    if (view !== 'day') return;
    
    try {
      setTripsLoading(true);
      const dateStr = formatDateForAPI(currentDate);
      
      const [tripsRes, driversRes, busesRes] = await Promise.all([
        axios.get(`${API}/api/trips`, {
          params: { date_from: dateStr, date_to: dateStr },
          ...getAuthHeaders()
        }),
        axios.get(`${API}/api/users`, getAuthHeaders()),
        axios.get(`${API}/api/buses`, getAuthHeaders())
      ]);
      
      setDayTrips(tripsRes.data || []);
      setDrivers(driversRes.data?.filter(u => u.is_driver === true) || []);
      setBuses(busesRes.data || []);
    } catch (error) {
      console.error('Error loading trips:', error);
      toast.error('Ошибка загрузки рейсов');
    } finally {
      setTripsLoading(false);
    }
  }, [currentDate, view]);

  useEffect(() => {
    if (view === 'day') {
      loadDayTrips();
    }
  }, [loadDayTrips, view]);

  // Add new trip row
  const addNewTripRow = () => {
    const dateStr = formatDateForAPI(currentDate);
    const newTrip = {
      _tempId: `new_${Date.now()}`,
      _isNew: true,
      date: dateStr,
      time_start: '09:00',
      time_end: '17:00',
      route_name: '',
      description: '',
      driver_id: '',
      bus_id: '',
      payment_amount: 0,
      driver_salary: 0,
      contact_phone: '',
      is_international: false,
      status: 'planned'
    };
    setDayTrips(prev => [...prev, newTrip]);
    // Focus on first editable cell of new row
    setTimeout(() => {
      setEditingCell({ tripId: newTrip._tempId, col: 'time_start' });
      setSelectedCell({ tripId: newTrip._tempId, col: 'time_start' });
    }, 50);
  };

  // Save trip (create or update)
  const saveTripRow = async (trip) => {
    try {
      const tripData = {
        date: trip.date,
        time_start: trip.time_start || '09:00',
        time_end: trip.time_end || '17:00',
        route_name: trip.route_name || '',
        description: trip.description || '',
        driver_id: trip.driver_id || null,
        bus_id: trip.bus_id || null,
        payment_amount: parseFloat(trip.payment_amount) || 0,
        driver_salary: parseFloat(trip.driver_salary) || 0,
        contact_phone: trip.contact_phone || '',
        is_international: trip.is_international || false,
        status: trip.status || 'planned'
      };

      if (trip._isNew) {
        await axios.post(`${API}/api/trips`, tripData, getAuthHeaders());
        toast.success('Рейс создан');
      } else {
        await axios.put(`${API}/api/trips/${trip.id}`, tripData, getAuthHeaders());
        toast.success('Рейс сохранён');
      }
      
      loadDayTrips();
    } catch (error) {
      console.error('Error saving trip:', error);
      toast.error('Ошибка сохранения рейса');
    }
  };

  // Delete trip
  const deleteTripRow = async (trip) => {
    if (trip._isNew) {
      setDayTrips(prev => prev.filter(t => t._tempId !== trip._tempId));
      return;
    }
    
    if (!window.confirm('Удалить рейс?')) return;
    
    try {
      await axios.delete(`${API}/api/trips/${trip.id}`, getAuthHeaders());
      toast.success('Рейс удалён');
      loadDayTrips();
    } catch (error) {
      console.error('Error deleting trip:', error);
      toast.error('Ошибка удаления');
    }
  };

  // Handle cell change
  const handleTripCellChange = (tripId, field, value) => {
    setDayTrips(prev => prev.map(t => {
      const id = t.id || t._tempId;
      if (id === tripId) {
        const updated = { ...t, [field]: value, _modified: !t._isNew };
        return updated;
      }
      return t;
    }));
  };

  // Get trip ID helper
  const getTripId = (trip) => trip.id || trip._tempId;

  // Render cell for trip
  const renderTripCell = (trip, column) => {
    const tripId = getTripId(trip);
    const value = trip[column.key];
    const isEditing = editingCell?.tripId === tripId && editingCell?.col === column.key;

    // Time input
    if (column.type === 'time') {
      if (isEditing) {
        return (
          <input
            type="time"
            defaultValue={value || '09:00'}
            autoFocus
            onBlur={(e) => {
              handleTripCellChange(tripId, column.key, e.target.value);
              setEditingCell(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Escape') {
                handleTripCellChange(tripId, column.key, e.target.value);
                setEditingCell(null);
              }
            }}
            className="w-full h-full border-0 bg-blue-50 text-sm px-1"
          />
        );
      }
      return <span className="text-sm font-mono">{value || '—'}</span>;
    }

    // Text input
    if (column.type === 'text') {
      if (isEditing) {
        return (
          <input
            type="text"
            defaultValue={value || ''}
            autoFocus
            onBlur={(e) => {
              handleTripCellChange(tripId, column.key, e.target.value);
              setEditingCell(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Escape') {
                handleTripCellChange(tripId, column.key, e.target.value);
                setEditingCell(null);
              }
            }}
            className="w-full h-full border-0 bg-blue-50 text-sm px-1"
          />
        );
      }
      return <span className="text-sm truncate">{value || '—'}</span>;
    }

    // Textarea
    if (column.type === 'textarea') {
      if (isEditing) {
        return (
          <textarea
            defaultValue={value || ''}
            autoFocus
            onBlur={(e) => {
              handleTripCellChange(tripId, column.key, e.target.value);
              setEditingCell(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape' || (e.key === 'Enter' && e.ctrlKey)) {
                handleTripCellChange(tripId, column.key, e.target.value);
                setEditingCell(null);
              }
            }}
            className="w-full border-0 bg-blue-50 text-sm px-1 resize-none"
            style={{ minHeight: '50px' }}
          />
        );
      }
      return <span className="text-sm text-gray-600 line-clamp-2">{value || '—'}</span>;
    }

    // Date input
    if (column.type === 'date') {
      if (isEditing) {
        return (
          <input
            type="date"
            defaultValue={value || ''}
            autoFocus
            onBlur={(e) => {
              handleTripCellChange(tripId, column.key, e.target.value);
              setEditingCell(null);
            }}
            onChange={(e) => {
              handleTripCellChange(tripId, column.key, e.target.value);
              setEditingCell(null);
            }}
            className="w-full h-full border-0 bg-blue-50 text-sm px-1"
          />
        );
      }
      // Format date for display
      const displayDate = value ? new Date(value + 'T00:00:00').toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) : '—';
      return <span className="text-sm">{displayDate}</span>;
    }

    // Number input
    if (column.type === 'number') {
      if (isEditing) {
        return (
          <input
            type="number"
            defaultValue={value || 0}
            autoFocus
            onBlur={(e) => {
              handleTripCellChange(tripId, column.key, parseFloat(e.target.value) || 0);
              setEditingCell(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Escape') {
                handleTripCellChange(tripId, column.key, parseFloat(e.target.value) || 0);
                setEditingCell(null);
              }
            }}
            className="w-full h-full border-0 bg-blue-50 text-sm px-1 text-right"
          />
        );
      }
      return <span className="text-sm text-right block">{value || 0}</span>;
    }

    // Select - Driver
    if (column.key === 'driver_id') {
      const driver = drivers.find(d => d.id === value);
      if (isEditing) {
        return (
          <select
            defaultValue={value || ''}
            autoFocus
            onChange={(e) => {
              handleTripCellChange(tripId, column.key, e.target.value);
              setEditingCell(null);
            }}
            onBlur={() => setEditingCell(null)}
            className="w-full h-full border-0 bg-blue-50 text-sm"
          >
            <option value="">— Водитель —</option>
            {drivers.map(d => (
              <option key={d.id} value={d.id}>{d.full_name || d.username}</option>
            ))}
          </select>
        );
      }
      return <span className="text-sm truncate">{driver?.full_name || driver?.username || '—'}</span>;
    }

    // Select - Bus (by bus_number)
    if (column.key === 'bus_number') {
      if (isEditing) {
        return (
          <select
            defaultValue={value || ''}
            autoFocus
            onChange={(e) => {
              const selectedBus = buses.find(b => b.number === e.target.value);
              handleTripCellChange(tripId, 'bus_number', e.target.value);
              if (selectedBus) {
                handleTripCellChange(tripId, 'bus_id', selectedBus.id);
              }
              setEditingCell(null);
            }}
            onBlur={() => setEditingCell(null)}
            className="w-full h-full border-0 bg-blue-50 text-sm"
          >
            <option value="">— Автобус —</option>
            {buses.map(b => (
              <option key={b.id} value={b.number}>{b.number}</option>
            ))}
          </select>
        );
      }
      return <span className="text-sm truncate">{value || '—'}</span>;
    }

    // Select - Bus (by bus_id)
    if (column.key === 'bus_id') {
      const bus = buses.find(b => b.id === value);
      if (isEditing) {
        return (
          <select
            defaultValue={value || ''}
            autoFocus
            onChange={(e) => {
              const selectedBus = buses.find(b => b.id === e.target.value);
              handleTripCellChange(tripId, column.key, e.target.value);
              if (selectedBus) {
                handleTripCellChange(tripId, 'bus_number', selectedBus.license_plate || selectedBus.number);
              }
              setEditingCell(null);
            }}
            onBlur={() => setEditingCell(null)}
            className="w-full h-full border-0 bg-blue-50 text-sm"
          >
            <option value="">— Автобус —</option>
            {buses.map(b => (
              <option key={b.id} value={b.id}>{b.license_plate || b.number}</option>
            ))}
          </select>
        );
      }
      return <span className="text-sm truncate">{bus?.license_plate || bus?.number || '—'}</span>;
    }

    // Checkbox
    if (column.type === 'checkbox') {
      return (
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => handleTripCellChange(tripId, column.key, e.target.checked)}
          className="w-4 h-4 accent-purple-600 cursor-pointer"
        />
      );
    }

    return <span className="text-sm">{value || '—'}</span>;
  };

  // Render day view - Excel style table with TRIPS
  const renderDayView = () => {
    const isToday = isSameDay(currentDate, new Date());
    // New trips (_isNew) go to the top, then sort saved trips by time
    const sortedTrips = [...dayTrips].sort((a, b) => {
      // New trips always on top
      if (a._isNew && !b._isNew) return -1;
      if (!a._isNew && b._isNew) return 1;
      // Both new or both saved - sort by time
      const timeA = a.time_start || '00:00';
      const timeB = b.time_start || '00:00';
      return timeA.localeCompare(timeB);
    });

    const hasChanges = dayTrips.some(t => t._isNew || t._modified);

    return (
      <div className="flex-1 overflow-auto bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className={`
              w-12 h-12 flex items-center justify-center rounded-xl text-xl font-bold
              ${isToday ? 'bg-white text-blue-600' : 'bg-blue-500'}
            `}>
              {currentDate.getDate()}
            </div>
            <div>
              <div className="text-lg font-semibold">
                {currentDate.toLocaleDateString('ru-RU', { weekday: 'long' })}
              </div>
              <div className="text-blue-200 text-sm">
                {currentDate.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <span className="bg-yellow-500 px-2 py-1 rounded text-xs font-medium animate-pulse">
                Несохранено
              </span>
            )}
            <span className="bg-blue-500 px-3 py-1 rounded-full text-sm">
              {sortedTrips.length} {sortedTrips.length === 1 ? 'рейс' : 
                sortedTrips.length < 5 ? 'рейса' : 'рейсов'}
            </span>
            <button
              onClick={addNewTripRow}
              className="flex items-center gap-2 bg-white text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors font-medium text-sm"
            >
              <Plus className="w-4 h-4" />
              Новый
            </button>
          </div>
        </div>

        {/* Excel-style table */}
        <div className="p-4">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-max">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-2 py-2 text-xs font-bold text-gray-600 w-8">#</th>
                    {TRIP_COLUMNS.map(col => (
                      <th 
                        key={col.key}
                        className="border border-gray-300 px-2 py-2 text-xs font-bold text-gray-600 text-left whitespace-nowrap"
                        style={{ width: col.width, minWidth: col.width }}
                      >
                        {col.label}
                      </th>
                    ))}
                    <th className="border border-gray-300 px-2 py-2 text-xs font-bold text-gray-600 w-24">⚙</th>
                  </tr>
                </thead>
                <tbody>
                  {tripsLoading ? (
                    <tr>
                      <td colSpan={TRIP_COLUMNS.length + 2} className="text-center py-8 text-gray-500">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto" />
                      </td>
                    </tr>
                  ) : sortedTrips.length === 0 ? (
                    <tr>
                      <td colSpan={TRIP_COLUMNS.length + 2} className="text-center py-12 text-gray-400">
                        <div className="flex flex-col items-center gap-2">
                          <CalendarIcon className="w-12 h-12 text-gray-300" />
                          <span>Нет рейсов на этот день</span>
                          <button
                            onClick={addNewTripRow}
                            className="mt-2 text-blue-500 hover:text-blue-600 font-medium text-sm"
                          >
                            + Добавить рейс
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    sortedTrips.map((trip, index) => {
                      const tripId = getTripId(trip);
                      const isNew = trip._isNew;
                      const isModified = trip._modified && !trip._isNew;
                      const isSelected = selectedCell?.tripId === tripId;
                      
                      return (
                        <tr 
                          key={tripId}
                          className={`
                            ${isNew ? 'bg-green-50' : isModified ? 'bg-yellow-50' : 'bg-white'}
                            ${isSelected ? 'ring-2 ring-inset ring-blue-400' : ''}
                            hover:bg-blue-50 transition-colors
                          `}
                        >
                          <td className="border border-gray-300 px-2 py-1.5 text-center text-xs text-gray-500 bg-gray-50">
                            {index + 1}
                          </td>
                          {TRIP_COLUMNS.map(col => (
                            <td 
                              key={col.key}
                              className={`border border-gray-300 p-0 cursor-cell ${
                                selectedCell?.tripId === tripId && selectedCell?.col === col.key 
                                  ? 'bg-blue-100 ring-2 ring-blue-500 ring-inset' 
                                  : ''
                              }`}
                              style={{ width: col.width, minWidth: col.width }}
                              onClick={() => {
                                setSelectedCell({ tripId, col: col.key });
                                if (col.type !== 'checkbox') {
                                  setEditingCell({ tripId, col: col.key });
                                }
                              }}
                            >
                              <div className="px-1.5 py-1.5 min-h-[32px] flex items-center">
                                {renderTripCell(trip, col)}
                              </div>
                            </td>
                          ))}
                          <td className="border border-gray-300 px-1 py-1">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => saveTripRow(trip)}
                                className="p-1.5 hover:bg-green-100 rounded text-green-600"
                                title="Сохранить"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteTripRow(trip)}
                                className="p-1.5 hover:bg-red-100 rounded text-red-600"
                                title="Удалить"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Quick add button at bottom */}
          <button
            onClick={addNewTripRow}
            className="mt-3 w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Добавить рейс
          </button>
        </div>
      </div>
    );
  };

  const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 
                      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

  const getHeaderTitle = () => {
    if (view === 'month') {
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    } else if (view === 'week') {
      const startOfWeek = new Date(currentDate);
      const day = startOfWeek.getDay();
      startOfWeek.setDate(startOfWeek.getDate() - (day === 0 ? 6 : day - 1));
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      
      if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
        return `${startOfWeek.getDate()} - ${endOfWeek.getDate()} ${monthNames[startOfWeek.getMonth()]} ${startOfWeek.getFullYear()}`;
      }
      return `${startOfWeek.getDate()} ${monthNames[startOfWeek.getMonth()].slice(0, 3)} - ${endOfWeek.getDate()} ${monthNames[endOfWeek.getMonth()].slice(0, 3)} ${endOfWeek.getFullYear()}`;
    }
    return `${currentDate.getDate()} ${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  };

  return (
    <div className={`${fullWidth ? 'h-[calc(100vh-80px)]' : 'h-[calc(100vh-120px)]'} flex bg-white rounded-xl shadow-lg overflow-hidden`}>
      {/* Sidebar - hide for day view and fullWidth mode */}
      {showSidebar && view !== 'day' && !fullWidth && (
        <div className="w-64 border-r bg-gray-50/50 p-4 flex flex-col gap-4 overflow-y-auto">
          {/* Create button */}
          <button
            onClick={() => handleCreateEvent(new Date())}
            className="flex items-center gap-3 w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-2xl shadow-sm hover:shadow-md hover:border-blue-200 transition-all group"
          >
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center group-hover:scale-105 transition-transform">
              <Plus className="w-5 h-5 text-white" />
            </div>
            <span className="font-medium text-gray-700">Создать</span>
          </button>

          {/* Mini calendar */}
          <MiniCalendar 
            currentDate={currentDate} 
            onDateSelect={setCurrentDate}
            events={events}
          />

          {/* Calendars list */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center justify-between">
              Мои календари
              <button className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                <Plus className="w-4 h-4 text-gray-400" />
              </button>
            </h3>
            <div className="space-y-2">
              {calendars.map(cal => (
                <label key={cal.id} className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={cal.is_visible}
                    onChange={() => {}}
                    className="w-4 h-4 rounded border-gray-300"
                    style={{ accentColor: cal.color }}
                  />
                  <span 
                    className="w-3 h-3 rounded-sm" 
                    style={{ backgroundColor: cal.color }}
                  />
                  <span className="text-sm text-gray-700 flex-1">{cal.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors lg:hidden"
            >
              <GripVertical className="w-5 h-5 text-gray-500" />
            </button>
            
            <button
              onClick={goToToday}
              className="px-4 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Сегодня
            </button>
            
            <div className="flex items-center">
              <button
                onClick={navigatePrev}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={navigateNext}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <h1 className="text-xl font-semibold text-gray-800 ml-2">
              {getHeaderTitle()}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            {/* View switcher */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              {[
                { id: 'day', label: 'День' },
                { id: 'week', label: 'Неделя' },
                { id: 'month', label: 'Месяц' }
              ].map(v => (
                <button
                  key={v.id}
                  onClick={() => setView(v.id)}
                  className={`
                    px-3 py-1.5 text-sm font-medium rounded-md transition-all
                    ${view === v.id 
                      ? 'bg-white text-gray-800 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-800'}
                  `}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Calendar content */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        ) : (
          <>
            {view === 'month' && renderMonthView()}
            {view === 'week' && renderWeekView()}
            {view === 'day' && renderDayView()}
          </>
        )}
      </div>

      {/* Event Modal */}
      <EventModal
        isOpen={showEventModal}
        onClose={() => {
          setShowEventModal(false);
          setSelectedEvent(null);
        }}
        event={selectedEvent}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
        calendars={calendars}
      />
    </div>
  );
}
